import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, Button, Grid, Select, FormControlLabel, InputLabel, MenuItem, Switch, TextField, Tabs, Tab, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import { useHistory, useParams } from 'react-router';
import React from 'react';
import AccessDeniedView from '../components/AccessDeniedView';
import RecordDetails, { RecordDetailsContext } from '../components/RecordDetails';
import LocalizedTextSetField from '../components/LocalizedTextField';
import { RulesetPreview } from '../components/RulesetPreview';
import NavigateBackBar from '../components/NavigateBackBar';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';

import { DataAPIContext, GET_ONE } from '../api';
import { DELETE, UPDATE } from '../api/dataProvider';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import LicenseeFormField from '../components/LicenseeFormField';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { ContextStore } from '../store/contextStore';
import { TEMPLATE_LANGUAGE_JINJA2, TEMPLATE_LANGUAGE_DJANGO, TEMPLATE_LANGUAGES } from './utils';
import NotificationStringLoading from './NotificationStringLoading';
import TabPanel, { a11yProps } from '../components/TabPanel';
import UsedByPanel from '../components/UsedByPanel';
import TestRenderDialog from './TestRenderDialog';
import useConfirmDialog from '../components/useConfirmDialog';

const FORMAT_INDEX_TO_NAME = ['html', 'markdown', 'plaintext'];

const useStyles = makeStyles((theme) => ({
    root: {
        position: 'relative',
        // height: `calc(${theme.cp.contentHeight} - 0.25rem)`,
        overflowY: 'auto',
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 3.5rem)`,
        },
        margin: theme.spacing(2),
    },
    recordListWrapper: {
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 15rem) !important`,
        },
    },
    recordList: {
        border: 'none !important',
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        height: `calc(${theme.cp.contentHeight} - 11.5rem) !important`,
    },
}));

const NotificatonStringEditForm = (props) => {
    const [confirmDelete, confirmDeleteDialog] = useConfirmDialog();
    const history = useHistory();
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'notification_string';
    const theme = useTheme();
    const classes = useStyles();
    const urlParams = useParams();
    const normalizedData = { ...props.data, template_language: props.data?.template_language || TEMPLATE_LANGUAGE_JINJA2 };
    const [string, setString] = React.useState(normalizedData);
    const [initialData, setInitialData] = React.useState(normalizedData);
    const [, setSyncActive] = React.useState(false);
    const [currentFormat, setCurrentFormat] = React.useState(0);
    const [expandedLanguages, setExpandedLanguages] = React.useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { hasPerm } = React.useContext(ContextStore);
    const hasWritePerm = hasPerm('notification', 'write')

    const onLicenseeChange = (id) => {
        setString((prev) => ({ ...prev, tour_operator: id }));
    };

    const [mainTab, setMainTab] = React.useState(0);
    const [testRenderOpen, setTestRenderOpen] = React.useState(false);

    const hasChanges = JSON.stringify(string) !== JSON.stringify(initialData);

    React.useEffect(() => {
        if (urlParams.id && (!string || !string.id)) {
            setSyncActive(true);
            dataAPI(GET_ONE, resource, { id: urlParams.id })
                .then((res) => {
                    // console.log('get one', urlParams.id, res);
                    setString({ ...res.data, template_language: res.data.template_language || TEMPLATE_LANGUAGE_JINJA2 });
                })
                .finally(() => setSyncActive(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlParams.id]);

    const onCurrentFormatChange = (e, nv) => {
        // console.log('nsedit: current format change', e, nv);
        setCurrentFormat(nv);
    }

    const onExpandedLanguagesChange = (ev) => {
        // console.log('nsedit: on expanded languages change', ev.target.checked, expandedLanguages);
        setExpandedLanguages(ev.target.checked);
        ev.preventDefault();
        ev.stopPropagation();
        return false;
    }

    const onTextChange = (e) => {
        setString((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }

    const onSelectChange = (e) => {
        setString((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }

    const onSwitchChange = (e) => {
        setString((prev) => ({ ...prev, [e.target.name]: e.target.checked }));
    }

    const onRulesetSelect = (e, nv) => {
        setString((prev) => ({ ...prev, ruleset: nv }));
    }

    const onTypeSelect = (e, nv) => {
        setString((prev) => ({ ...prev, type: nv }));
    }

    const onSave = () => {
        setSyncActive(true);
        dataAPI(UPDATE, resource, { id: props.id, data: string }).then((res) => {
            if (res.statusCode === 200) {
                enqueueSnackbar('Notification String saved', { variant: 'success' });
                const next = { ...res.data, template_language: res.data.template_language || TEMPLATE_LANGUAGE_JINJA2 };
                setString(next);
                setInitialData(next);
            } else if (res.statusCode === 406) {
                enqueueSnackbar(res.data.message, { variant: 'error' });
            } else {
                enqueueSnackbar(`Error saving Notification String (${res.statusCode})`, { variant: 'error' });
            }
        }).catch((err) => {
            enqueueSnackbar('Error saving Notification String', { variant: 'error' });
        })
        .finally(() => setSyncActive(false));
    }

    const onDelete = async () => {
        if (await confirmDelete({ body: 'Are you sure you want to delete this notification string?' })) {
            setSyncActive(true);
            dataAPI(DELETE, resource, { id: props.id }).then((res) => {
                if (res.statusCode === 204) {
                    enqueueSnackbar('Notification String deleted', { variant: 'success' });
                    history.push('/notifications/strings');
                } else {
                    enqueueSnackbar('Error deleting Notification String', { variant: 'error' });
                }
            }).catch((err) => {
                enqueueSnackbar('Error deleting Notification String', { variant: 'error' });
            })
            .finally(() => setSyncActive(false));
        }
    }

    // CP-8423: back to the strings list, same target as the NavigateBackBar arrow.
    const onCancel = () => {
        history.push('/notifications/strings');
    };

    // console.log('nsedit', expandedLanguages, currentFormat);
    return (<FormLicenseeContext.Provider value={toLicenseeId(string.tour_operator)}>
        <div className={classes.root}>
        {confirmDeleteDialog}
        <Grid container direction="row" spacing={2}>
            {/* <Grid item xs={12}>
                <FixedLinearProgress visibility={syncActive} color="primary"/>
            </Grid> */}
            <Grid item xs={12}>
                <NavigateBackBar 
                    label='Edit Notification String' 
                    url={'/notifications/strings'}
                    backButtonNavbarStyle={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 !important',
                        paddingTop: '0 !important',
                    }}
                    labelStyle={{ 
                        fontSize: '1.25rem', 
                        fontWeight: 500, 
                        lineHeight: 1.6, 
                        marginLeft: 0
                    }} 
                />
            </Grid>
            {props.id && <Grid item xs={12}>
                <Tabs value={mainTab} onChange={(ev, newTab) => setMainTab(newTab)} indicatorColor="primary" textColor="primary">
                    <Tab label="General" {...a11yProps(0)} />
                    <Tab label="Used by" {...a11yProps(1)} />
                </Tabs>
            </Grid>}
            {mainTab === 0 && <><Grid item xs={12}>
                <LicenseeFormField value={string.tour_operator} onChange={onLicenseeChange} disabled={!hasWritePerm} />
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    disabled={props.disabled}
                    control={
                        <Switch name="enabled" checked={string.enabled} onChange={onSwitchChange} />
                    }
                    label="Enabled"
                />
            </Grid>
            <Grid item xs={12}>
                <FormControl
                    fullWidth
                    variant="filled"
                >
                    <RemoteAutocomplete
                        resource="notification_string_type"
                        urlSuffix={null}
                        label="Type"
                        variant="filled"
                        size="small"
                        fullWidth
                        onChange={onTypeSelect}
                        value={string.type}
                        disabled={props.disabled}
                    />
                    <FormHelperText>
                        {!string.type
                            ? 'Standalone snippet — no type. Only includable by codename via {% notification_string "codename" %}; excluded from {% notification_strings_block %} bulk rendering.'
                            : `Code name: ${string.type?.codename}`}
                    </FormHelperText>
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <TextField
                    name="sequence"
                    label="Sequence"
                    variant="filled"
                    fullWidth
                    value={string.sequence}
                    onChange={onTextChange}
                    disabled={props.disabled}
                    inputProps={{ type: 'number' }}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    name="name"
                    label="Name"
                    variant="filled"
                    fullWidth
                    value={string.name}
                    onChange={onTextChange}
                    disabled={props.disabled}
                    error={!string.name}
                    helperText={!string.name ? "Name is required" : ""}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    name="codename"
                    label="Code Name"
                    variant="filled"
                    fullWidth
                    value={string.codename}
                    onChange={onTextChange}
                    disabled={props.disabled}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <RemoteAutocomplete
                    getOptionLabel={opt => (opt.name || "")}
                    resource="notification_ruleset"
                    urlSuffix={null}
                    label="Ruleset"
                    variant="filled"
                    size="small"
                    fullWidth
                    onChange={onRulesetSelect}
                    value={string.ruleset}
                    disabled={props.disabled}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <RulesetPreview id={string.ruleset?.id} ruleset={string.ruleset} />
            </Grid>
            <Grid item xs={12}>
                <Tabs value={currentFormat} onChange={onCurrentFormatChange}>
                    <Tab label="HTML" {...a11yProps(0)} />
                    <Tab label="Markdown" {...a11yProps(1)} />
                    <Tab label="Plain text" {...a11yProps(2)} />
                    <Box sx={{ marginLeft: theme.spacing(8), display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                            disabled={props.disabled}
                            control={
                                <Switch
                                    size="small"
                                    checked={expandedLanguages}
                                    onChange={onExpandedLanguagesChange}
                                    />
                            }
                            label="Expand"
                        />
                    </Box>
                </Tabs>
                <TabPanel value={+currentFormat} index={0}>
                    <LocalizedTextSetField
                        expanded={expandedLanguages}
                        disabled={props.disabled}
                        variant="filled"
                        size="small"
                        label="HTML value"
                        name="html"
                        value={string.html}
                        fullWidth
                        onChange={onTextChange}
                    />
                </TabPanel>
                <TabPanel value={+currentFormat} index={1}>
                    <LocalizedTextSetField
                        expanded={expandedLanguages}
                        disabled={props.disabled}
                        variant="filled"
                        size="small"
                        label="Markdown value"
                        name="markdown"
                        value={string.markdown}
                        fullWidth
                        onChange={onTextChange}
                    />
                </TabPanel>
                <TabPanel value={+currentFormat} index={2}>
                    <LocalizedTextSetField
                        expanded={expandedLanguages}
                        disabled={props.disabled}
                        variant="filled"
                        size="small"
                        label="Plain text value"
                        name="plaintext"
                        value={string.plaintext}
                        fullWidth
                        onChange={onTextChange}
                    />
                </TabPanel>
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    disabled={props.disabled}
                    control={
                        <Switch name="is_template" checked={string.is_template} onChange={onSwitchChange} />
                    }
                    label="Value is a template"
                />
            </Grid>
            {string.is_template && (
            <Grid item xs={12}>
                <FormControl variant="filled" size="small" fullWidth>
                    <InputLabel id="edit-template-language-label">Template language</InputLabel>
                    <Select
                        labelId="edit-template-language-label"
                        name="template_language"
                        value={string.template_language || TEMPLATE_LANGUAGE_JINJA2}
                        onChange={onSelectChange}
                        label="Template language"
                        disabled={props.disabled}
                    >
                        <MenuItem value={TEMPLATE_LANGUAGE_JINJA2}>{TEMPLATE_LANGUAGES[TEMPLATE_LANGUAGE_JINJA2]}</MenuItem>
                        <MenuItem value={TEMPLATE_LANGUAGE_DJANGO}>{TEMPLATE_LANGUAGES[TEMPLATE_LANGUAGE_DJANGO]}</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            )}
            <Grid item xs={12}>
                <FormActionButtons
                    mode="edit"
                    size="medium"
                    onSubmit={onSave}
                    submitDisabled={!hasWritePerm || !hasChanges || !string.name}
                    onCancel={onCancel}
                    onDelete={onDelete}
                    deleteDisabled={!hasWritePerm}
                    deletePerm="notification.delete"
                    extraActions={
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<PlayArrowIcon />}
                            onClick={() => setTestRenderOpen(true)}
                            disabled={!props.id}
                        >
                            Test Render
                        </Button>
                    }
                />
            </Grid>
            </>}
            {props.id && mainTab === 1 && <Grid item xs={12}>
                <UsedByPanel resource={resource} id={props.id} />
            </Grid>}
        </Grid>
        <TestRenderDialog
            open={testRenderOpen}
            onClose={() => setTestRenderOpen(false)}
            notificationStringId={props.id}
            draftBody={string[FORMAT_INDEX_TO_NAME[currentFormat]]}
            initialFormat={FORMAT_INDEX_TO_NAME[currentFormat]}
        />
    </div>
    </FormLicenseeContext.Provider>);
}

const NotificationStringEdit = (props) => {
    const resource = 'notification_string';
    const routeParams = useParams();
    const id = routeParams.id;
    const { hasPerm } = React.useContext(ContextStore);

    if (!hasPerm('notification', 'read')) {
        return <AccessDeniedView />
    }
    return (
        <RecordDetails resource={resource} id={id} fullResponse>
            <RecordDetailsContext.Consumer>{(res) => (res.statusCode === 200 ? <NotificatonStringEditForm {...props} resource={resource} id={id} data={res.data} /> : <NotificationStringLoading {...props} resource={resource} id={id} res={res} />)}</RecordDetailsContext.Consumer>
        </RecordDetails>
    );
};

export default NotificationStringEdit;
